import { entries, isNotNil } from "client/Misc/utils";

export function setPropertiesPreservingRefWhenUnchanged<T extends Record<string, unknown>>(
	currentState: T,
	patch: Partial<T>
): T {
	if (currentState === patch) {
		return currentState;
	}

	for (const entry of entries(patch)) {
		const {
			key: keyToPatch,
			value: valueToPatch
		} = entry;

		if (currentState[keyToPatch] !== valueToPatch) {
			return {
				...currentState,
				...patch
			};
		}
	}

	return currentState;
}

export class ModifiedCollectionBuilder<TItem> {
	private readonly _referenceCollection: readonly TItem[];
	private _modifiedCollection: TItem[] | undefined;
	private _resultLength: number = 0;

	constructor(referenceCollection: (readonly TItem[])) {
		this._referenceCollection = referenceCollection;
		this._resultLength = 0;
		this._modifiedCollection = undefined;
	}

	public push(item: TItem): void {
		if (this._modifiedCollection) {
			this._modifiedCollection.push(item);
			return;
		}

		if (this._referenceCollection.length > this._resultLength) {
			const itemIndexToCompare: number = this._resultLength;

			if (this._referenceCollection[itemIndexToCompare] === item) {
				++this._resultLength;
				return;
			}
		}

		this._modifiedCollection = this._referenceCollection.slice(0, this._resultLength);
		this._modifiedCollection.push(item);
	}

	public remove(predicate: (item: TItem) => boolean): void {
		if (this._modifiedCollection) {

			const indicesToRemove: number[] = this._modifiedCollection
				.map(
					(item, index) => predicate(item)
						? index
						: null
				)
				.filter(isNotNil);

			for (const itemIndex of indicesToRemove.reverse()) {
				this._modifiedCollection.splice(itemIndex, 1);
			}

			return;
		}

		const hasItemToRemove: boolean = this._referenceCollection.some(predicate);

		if (!hasItemToRemove) {
			return;
		}

		this._modifiedCollection = this._referenceCollection.filter(x => !predicate(x));
	}

	public result(): readonly TItem[] {
		if (this._modifiedCollection) {
			return this._modifiedCollection;
		}

		if (this._referenceCollection.length === this._resultLength) {
			return this._referenceCollection;
		}

		if (this._referenceCollection.length > this._resultLength) {
			return this._referenceCollection.slice(0, this._resultLength);
		}

		throw new Error("Original collection is smaller than the result.");
	}
}